import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { updateHomepageFooter } from '../../../../../../store/actions/admin/create-event';
import { EventsState } from '../../../../../../store/types';
import { LanguagesAbbr, PageModuleType } from '../../../../../../types/working-model';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import Controls, { InitialControlsDisplay } from '../../../session/editor/moduleControls/controls';
import NavItem, { checkLinkedSectionIsOn, filterActiveModules } from '../../../../../general-ui/nav-item/nav-item';
import { ReactComponent as NowStreamingLogo } from '../../../../../../images/now-streaming-on-brandlive.svg';
import { getFooterStylingOverrides } from '../../../../../live-event/utils';
import EditSectionColorsModal, { EContentColorOverrideNames } from '../../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import { EUploadOptions, ImageUploader, ImageUploadOptions } from '../../../../../general-ui/image-uploader/image-uploader';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';

import '../../../../../../scss/live-event/base/marketing-page/footer.scss';
import './footer.scss';

interface IEditorFooterProps {
	handleScroll: (moduleId?: number, p?: PageModuleType) => void;
	template: string;
	baseLanguage: LanguagesAbbr;
	language: LanguagesAbbr
}

const EditorFooter: React.FC<IEditorFooterProps> = ({ handleScroll, template, baseLanguage, language }) => {
	const {
		CreateEventReducer: { workingEvent },
	} = useSelector((event: EventsState) => event);
	const [logo, setLogo] = useState<string | null | undefined>(workingEvent?.homepage?.footer?.logo);
	const [openEditLogoModal, setOpenEditLogoModal] = useState<ImageUploadOptions>(null);
	const [openEditColor, setOpenEditColor] = useState<EContentColorOverrideNames | null>(null);
	const [activePageModules, setActivePageModules] = useState(filterActiveModules(workingEvent?.homepage?.modules));

	const dispatch = useDispatch();

	useEffect(() => {
		setActivePageModules(filterActiveModules(workingEvent?.homepage?.modules));
	}, [workingEvent?.homepage?.modules]);

	const { t } = useTranslate(['homepage', 'landing']);

	const saveLogo = useCallback((image: { original: string, edited: string; }) => {
		if (!workingEvent?.homepage?.footer) return;
		// TODO: refactor to keep original image for future editing
		dispatch(
			updateHomepageFooter({ ...workingEvent.homepage.footer, logo: image.edited })
		);
		setLogo(image.edited);
	}, [dispatch, workingEvent]);

	const handleColorOverrides = (colorKey: string, name: EContentColorOverrideNames): void => {
		if (!workingEvent?.homepage || !workingEvent?.homepage?.footer) return;
		let workingStylingOverrides = { ...workingEvent?.homepage.footer?.styling_overrides };

		const isRestoringDefault = colorKey === "-color";

		if (name === EContentColorOverrideNames.navItems) {
			if (!workingStylingOverrides?.content) {
				workingStylingOverrides.content = {
					navItems: { color: isRestoringDefault ? "" : colorKey },
				};
			} else {
				workingStylingOverrides = {
					...workingStylingOverrides, content: {
						...workingStylingOverrides.content,
						navItems: { color: isRestoringDefault ? "" : colorKey },
					}
				};
			}
		}

		if (name === EContentColorOverrideNames.blLogo) {
			if (!workingStylingOverrides?.content) {
				workingStylingOverrides.content = {
					blLogoColor: isRestoringDefault ? "" : colorKey,
				};
			} else {
				workingStylingOverrides = {
					...workingStylingOverrides, content: {
						...workingStylingOverrides.content,
						blLogoColor: isRestoringDefault ? "" : colorKey,
					}
				};
			}
		}

		if (workingStylingOverrides && colorKey) {
			dispatch(
				updateHomepageFooter({
					...workingEvent.homepage.footer,
					styling_overrides: workingStylingOverrides
				})
			);
		}
	};

	const footerControllerDisplay: InitialControlsDisplay = {
		editLayout: true,
		editColors: true,
	};

	const navItems = workingEvent?.homepage?.footer?.navItems;
	const {
		backgroundColor,
		backgroundImg,
		mainTextOverrides,
		blLogoColor
	} = getFooterStylingOverrides(workingEvent?.homepage?.footer?.styling_overrides);

	return workingEvent?.homepage ? (
		<div className={
			classNames(
				'editor-wrapper footer marketing-page-footer',
				template,
				backgroundColor
			)}
		>
			<Controls
				display={footerControllerDisplay}
				openEditModal={() => 'leave blank'}
				module={workingEvent.homepage.footer}
				baseLanguage={baseLanguage}
				language={language}
			/>
			<footer className={classNames("footer-container")} style={backgroundImg}>
				<div className="footer-left">
					{logo ? (
						<img
							className="footer-logo"
							alt="Logo"
							onClick={() => setOpenEditLogoModal(EUploadOptions.upload)}
							src={logo}
						/>
					) : (
						<h3
							onClick={() => setOpenEditLogoModal(EUploadOptions.upload)}
						>
							{t("Your Logo")}
						</h3>
					)}
					<div className='edit-button-container'>
						{logo ? (
							<button onClick={() => setOpenEditLogoModal(EUploadOptions.edit)} className="round edit-button">
								<Icon name={ICONS.IMAGE_CROP} color={COLORS.WHITE} size={14} />
							</button>
						) : null}
						<button className="round replace-image" onClick={() => setOpenEditLogoModal(EUploadOptions.upload)}>
							<Icon name={ICONS.IMAGE} color={COLORS.GRAY} size={16} />
						</button>
					</div>
				</div>
				<nav className="footer-nav">
					{navItems && navItems.map(navItem => {
						if (checkLinkedSectionIsOn(navItem, activePageModules)) {
							// get the overrideColor to reuse for the hover class
							const overrideColor = mainTextOverrides?.split('-')[0];
							const labelOverride = t(`landing:footer.${navItem.button_name.en}`, '');

							return (
								<NavItem
									navItem={navItem}
									key={navItem.button_name.base}
									handleScroll={handleScroll}
									className={classNames("footer-nav-item", mainTextOverrides, { [`${overrideColor}-hover`]: mainTextOverrides })}
									isEditor={true}
									labelOverride={labelOverride}
								/>
							);
						}

					})}
					<button onClick={() => setOpenEditColor(EContentColorOverrideNames.navItems)} className="round edit-button">
						<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={12} />
					</button>
				</nav>
				<div className="footer-right">
					<div className="footer-author">
						<NowStreamingLogo className={classNames("bl-logo", blLogoColor)}/>
						<button onClick={() => setOpenEditColor(EContentColorOverrideNames.blLogo)} className="round edit-button">
							<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={12} />
						</button>
					</div>
				</div>
				{/* remove copyright pending further discussions*/}
				{/* <div className="footer-copyright"> */}
				{/* <p>&copy; {new Date().getFullYear()}. All rights reserved. Created by Brandlive.com</p> */}
				{/* </div> */}
			</footer>

			<ImageUploader
				originalImage={logo}
				onSave={saveLogo}
				onClose={() => setOpenEditLogoModal(null)}
				fileTitle={"Logo"}
				open={openEditLogoModal}
			/>

			{openEditColor ? (
				<EditSectionColorsModal
					open={openEditColor !== null}
					onClose={() => setOpenEditColor(null)}
					onFinish={(colorKey) => handleColorOverrides(colorKey, openEditColor)}
					title={openEditColor}
				/>
			) : null}
		</div>
	) : (
		<div></div>
	);
};

export default EditorFooter;
